import React, { useCallback } from 'react';
import { useModal } from 'mui-modal-provider';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  AppointmentRequestButton as AppointmentRequestButtonView,
} from './appointment-request-button';
import { AppointmentRegistrationModal } from '../appointment-registration-modal';
import { AskLoginModal } from '../../../common/ask-login-modal';
import { appointmentSettings, AppointmentSettingsProps } from '../../../common/appointment-settings';
import { useGoBack } from '../../../../hooks/use-go-back';
import { InternalLinks } from '../../../../constants';
import { setRedirectUrl } from '../../../../utils/redirect/redirect';
import { pushStoreCodeIfEnabled } from '../../../../utils';

const AppointmentRequestButtonComponent = ({
  isLoggedIn, appointments, isLoading, isError, updateAppointments, appointmentUrl, isRetailer, storeCode, testId,
}: AppointmentSettingsProps): JSX.Element => {
  const { showModal } = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPreviousLocationState } = useGoBack();

  const pushStoreCode = useCallback(() => {
    if (storeCode) {
      pushStoreCodeIfEnabled(storeCode);
    }
  }, [storeCode, pushStoreCodeIfEnabled]);

  const onNavigate = (url: string): void => {
    navigate(url, { state: hasPreviousLocationState });
  };

  const onRegistrationComplete = useCallback((): void => {
    pushStoreCode();
    onNavigate(InternalLinks.appointment);
  }, [onNavigate, pushStoreCode]);

  const handleNavigateToRegistration = useCallback(() => {
    setRedirectUrl(location.pathname);
    navigate(InternalLinks.registration);
  }, [navigate, location.pathname]);

  const handleNavigateToLogin = useCallback(() => {
    setRedirectUrl(location.pathname);
    navigate(InternalLinks.login);
  }, [navigate, location.pathname]);

  const handleButtonClick = () => {
    pushStoreCode();

    if (isLoggedIn && appointmentUrl) return;

    if (isLoggedIn) {
      if (isRetailer) {
        onNavigate(`${InternalLinks.ecpLocator}?ecpService=fit`);
      } else {
        showModal(AppointmentRegistrationModal, {
          updateAppointments: updateAppointments!,
          onRegistrationComplete,
          goNextButtonTestIdsByStep: {
            appointmentDate: 'ecp-request-stepper-next-button', // Don't remove this test id, it is used by YaMetrica
            appointmentCheck: 'ecp-request-stepper-confirm-button', // Don't remove this test id, it is used by YaMetrica
            appointmentSuccess: 'ecp-request-stepper-view-button', // Don't remove this test id, it is used by YaMetrica
          },
        });
      }
    } else {
      showModal(AskLoginModal, {
        onNavigateToRegistration: handleNavigateToRegistration,
        onNavigateToLogin: handleNavigateToLogin,
      });
    }
  };

  return (
    <AppointmentRequestButtonView
      isLoggedIn={isLoggedIn}
      appointmentUrl={ isLoggedIn ? appointmentUrl : undefined }
      isLoading={isLoading}
      isError={isError}
      appointments={appointments}
      onClick={handleButtonClick}
      testId={testId}
    />
  );
};

export const AppointmentRequestButton = appointmentSettings(AppointmentRequestButtonComponent);
